import { useRef, useCallback } from "react";
import httpClient, { AuthNotReadyError } from "../utils/httpClient";
import { BonusProps } from "../providers/types";
import { sleep } from "../utils/sleep";

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 2000;

export const useLoadBonuses = (
  setBonuses: (data: BonusProps[]) => void,
  setLoadingBonuses: (loading: boolean) => void,
  showError: (message: string) => void
) => {
  const inFlightRef = useRef<Promise<void> | null>(null);

  const loadBonuses = useCallback(
    async (roleType: string) => {
      if (!roleType) return;
      if (inFlightRef.current) return inFlightRef.current;

      const fetchOnce = async (): Promise<BonusProps[] | null> => {
        try {
          const res = await httpClient.get<BonusProps[]>(`/api/cba/bonuses`, {
            headers: { roleType },
          });
          return Array.isArray(res.data) ? res.data : null;
        } catch (e) {
          if (e instanceof AuthNotReadyError || (e as any)?.response?.status === 401) {
            return null; // Silent retry
          }
          throw e; // Real error
        }
      };

      setLoadingBonuses(true);

      inFlightRef.current = (async () => {
        try {
          let data = await fetchOnce();

          let attempts = 0;
          while (data === null && attempts < MAX_RETRIES) {
            await sleep(RETRY_DELAY_MS);
            data = await fetchOnce();
            attempts++;
          }

          if (data === null) {
            showError("An error occurred while loading bonuses. Please try again later.");
            setBonuses([]);
            return;
          }

          setBonuses(data);
        } catch (e: any) {
          console.error("Bonuses request failed:", e.message, e.response?.status);
          showError("An error occurred while loading bonuses. Please try again later.");
          setBonuses([]);
        } finally {
          inFlightRef.current = null;
          setLoadingBonuses(false);
        }
      })();

      return inFlightRef.current;
    },
    [setBonuses, setLoadingBonuses, showError]
  );

  return { loadBonuses };
};